import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useDeviceType from "../../../custom-hooks/DeviceType";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import Button from "../../../component-lib/Molecules/button/Button";
import EventEHRSwitchButton from "../../../component-lib/Molecules/switch-button/EventEHRSwitchButton";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import {
  plusIcon,
  rightDirectionBlackIcon,
  searchIcon,
} from "../../../assets";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import {
  componentKey,
  setCloseMasterDataDialog,
} from "../SettingsSlice";
import {
  deleteMasterData,
  getMasterDataList,
  patchMasterData,
  postMasterData,
} from "../SettingsSaga";
import MasterDataDialog from "../dialogs/MasterDataDialog";

interface MasterDataScreenProps {
  type: string;
  title: string;
  nameLabel: string;
  addButtonLabel: string;
  dialogTitle: string;
}

const MasterDataScreen = ({
  type,
  title,
  nameLabel,
  addButtonLabel,
  dialogTitle,
}: MasterDataScreenProps) => {
  const { masterDataList, closeMasterDataDialog } = useSelector(
    (state: any) => state[componentKey]
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const deviceType = useDeviceType();

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [searchedValue, setSearchValue] = useState<string>("");
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);
  const [defaultRow, setDefaultRow] = useState<any>({});
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const columns = [
    {
      field: "name",
      label: <div>{nameLabel}</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.name}</p>
      ),
    },
    {
      field: "description",
      label: <div>Description</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.description || "-"}
        </p>
      ),
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => {
        const isActive = row?.status === "Active";
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <EventEHRSwitchButton
              checked={isActive}
              onChange={() => {
                dispatch(
                  patchMasterData({
                    id: row?.id,
                    payload: { status: isActive ? "InActive" : "Active" },
                  })
                );
              }}
            />
          </div>
        );
      },
    },
    {
      field: "createdAt",
      label: <div>Created At</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.createdAt
            ? moment(row?.createdAt).format("MM/DD/YYYY HH:mm")
            : "-"}
        </p>
      ),
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => (
        <div className="flex items-center">
          <MenuDropdown
            options={[
              {
                label: "Edit",
                onClickCb: () => {
                  setDefaultRow(row);
                  setDialogState("edit");
                },
              },
              {
                label: "Delete",
                onClickCb: () => {
                  setSelectedId(row?.id);
                  setOpenConfirmationDialog(true);
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const rows = masterDataList?.masterData?.map((data: any) => ({
    id: data?.id || "-",
    name: data?.name || "-",
    description: data?.description || "",
    status: data?.status || "-",
    createdAt: data?.createdAt || "",
  }));

  const fetchMasterData = () => {
    dispatch(
      getMasterDataList({
        pageNumber,
        limit: rowPerPage,
        searchValue: searchedValue?.trim(),
        type,
      })
    );
  };

  useEffect(() => {
    fetchMasterData();
  }, [type, pageNumber, rowPerPage, searchedValue]);

  useEffect(() => {
    if (closeMasterDataDialog) {
      dispatch(setCloseMasterDataDialog(false));
      setDialogState(null);
      setDefaultRow({});
      setOpenConfirmationDialog(false);
      setSelectedId(null);
      fetchMasterData();
    }
  }, [closeMasterDataDialog]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500);
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" || deviceType === "small-tablet" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading title={title} customStyle={{ color: "#1D1D1D" }} />
          </div>

          <div
            className={`flex ${deviceType === "mobile" || deviceType === "small-tablet" ? "w-full justify-end" : ""} gap-3`}
          >
            <div>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e: any) => handleSearchChange(e)}
                size="small"
                label="Search by name"
                fullWidth={deviceType === "mobile" ? "160px" : "300px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>
            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"add"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label={addButtonLabel}
              variant="contained"
              onClickCb={() => {
                setDefaultRow({});
                setDialogState("add");
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "180px",
              }}
            />
          </div>
        </div>

        <div style={{ overflow: "auto" }}>
          <Table
            columns={columns}
            rows={rows}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: masterDataList?.totalPages,
              totalCount: masterDataList?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>

      {dialogState && (
        <MasterDataDialog
          isOpen={!!dialogState}
          title={dialogState === "edit" ? `Edit ${title}` : dialogTitle}
          nameLabel={nameLabel}
          defaultData={defaultRow}
          handleClose={() => {
            setDialogState(null);
            setDefaultRow({});
          }}
          handleData={({ name, description, status }) => {
            const payload = {
              name,
              description,
              type,
              status,
            };
            if (dialogState === "edit" && defaultRow?.id) {
              dispatch(
                patchMasterData({
                  id: defaultRow.id,
                  payload,
                })
              );
            } else {
              dispatch(postMasterData(payload));
            }
          }}
        />
      )}

      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          alertTitle={`Delete ${title}`}
          alertDescription={`Are you sure you want to delete this ${title}? Please confirm to proceed.`}
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
              setSelectedId(null);
            } else if (action === "submit" && selectedId) {
              dispatch(deleteMasterData({ id: selectedId }));
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default MasterDataScreen;
